import React, { useRef, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { FileUp, Loader2, CheckCircle, AlertTriangle, FileText, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;

const SummaryRow = ({ label, value, testId }) => (
  <div className="flex justify-between text-sm py-1 border-b border-border/50">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono font-medium" data-testid={testId}>{value}</span>
  </div>
);

export const DayEndReportTab = ({ storeId, salesDate, tillReport, onApply, canUpload }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('store_id', storeId);
      form.append('sales_date', format(salesDate, 'yyyy-MM-dd'));
      const res = await axios.post(`${API}/till-report/parse`, form);
      onApply(res.data);
      toast.success('Day End report read - figures filled');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to read the Day End report';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const m = tillReport?.mapped;
  const r = tillReport?.report;

  return (
    <div className="space-y-4" data-testid="day-end-report-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Day End Report</CardTitle>
          <CardDescription>
            Upload the till Day End report (PDF) for {format(salesDate, 'dd MMM yyyy')}. Sales, Paid Outs and Tenders are filled automatically. The file is read and not stored.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="day-end-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading || !canUpload} data-testid="day-end-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
            {tillReport ? 'Upload Again (replace)' : 'Upload Day End Report'}
          </Button>
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-600" data-testid="day-end-error">
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" />{error}
            </div>
          )}
        </CardContent>
      </Card>

      {tillReport && (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="day-end-summary">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />Filled from report
              </CardTitle>
              <CardDescription>
                {r.till_system ? `Till: ${r.till_system} · ` : ''}{r.site_name} · Day End {r.report_date}{r.day_end_id ? ` · ID ${r.day_end_id}` : ''} · {r.file_name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {r.fuel_grades.map(g => (
                <SummaryRow key={g.name} label={`${g.name} (£${g.ppl.toFixed(3)})`} value={`${g.litres.toFixed(2)} L · ${gbp(g.value)}`} />
              ))}
              {m.fuel_volumes && (<>
                <SummaryRow label="Retail Sales" value={gbp(m.retail_sales)} testId="day-end-retail" />
                <SummaryRow label="Total Sales* (Z-Reading)" value={gbp(m.total_fuel_sales)} testId="day-end-z" />
                <SummaryRow label="Bunkering Sales" value={gbp(m.bunkering_sales)} testId="day-end-bunkering" />
              </>)}
              {m.bunkering && (
                <div className="py-1 border-b border-border/50" data-testid="day-end-bunkering-lines">
                  {[...Object.entries(m.bunkering.cards), ['Key Fuels', m.bunkering.key_fuels.transactions]].map(([card, txs]) =>
                    txs.map((t, i) => (
                      <div key={`${card}-${i}`} className="flex justify-between text-xs text-muted-foreground pl-3">
                        <span>{card} · {t.grade_name} · {t.volume.toFixed(2)} L</span>
                        <span className="font-mono">{gbp(t.amount)}</span>
                      </div>
                    ))
                  )}
                  {m.bunkering.key_fuels.shop_items_amount > 0 && (
                    <div className="flex justify-between text-xs text-muted-foreground pl-3">
                      <span>Key Fuels · Shop items</span><span className="font-mono">{gbp(m.bunkering.key_fuels.shop_items_amount)}</span>
                    </div>
                  )}
                </div>
              )}
              <SummaryRow label={`Shop Total Sales (${m.shop_total_label || (m.fuel_volumes ? 'Net Shop Sales' : 'Sub Total 1')})`} value={gbp(m.shop_total_sales)} testId="day-end-shop-total" />
              <SummaryRow label="Other Departments (auto)" value={gbp(m.other_departments_amount)} />
              {Object.entries(m.service_amounts).map(([k, v]) => (
                <SummaryRow key={k} label={k.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} value={gbp(v)} />
              ))}
              {m.tenders && (
                <div className="pt-2" data-testid="day-end-tenders">
                  <p className="text-xs font-semibold uppercase text-muted-foreground py-1">Tenders</p>
                  <SummaryRow label="Cash" value={gbp(m.tenders.payments.cash)} testId="day-end-tender-cash" />
                  <SummaryRow label="Voucher" value={gbp(m.tenders.payments.vouchers)} testId="day-end-tender-voucher" />
                  <SummaryRow label="Card Payments (BMS)" value={gbp(m.tenders.payments.card)} testId="day-end-tender-card" />
                  {m.fuel_volumes && <SummaryRow label="AMEX" value={gbp(m.tenders.payments.amex)} testId="day-end-tender-amex" />}
                  {m.cash_discrepancy !== null && m.cash_discrepancy !== undefined && (
                    <SummaryRow label={`Till cash ${m.cash_discrepancy >= 0 ? 'gain' : 'loss'}`} value={gbp(Math.abs(m.cash_discrepancy))} testId="day-end-cash-discrepancy" />
                  )}
                  {Object.entries(m.tenders.fuel_cards).map(([k, v]) => (
                    <SummaryRow key={k} label={k} value={gbp(v)} testId={`day-end-tender-${k.toLowerCase().replace(/\s+/g, '-')}`} />
                  ))}
                </div>
              )}
              {m.paid_outs && (
                <div className="pt-2" data-testid="day-end-paid-outs">
                  <p className="text-xs font-semibold uppercase text-muted-foreground py-1">Paid Outs</p>
                  <SummaryRow label="Lottery Prize Paid" value={gbp(m.paid_outs.lottery_prize)} testId="day-end-lottery-prize" />
                  <SummaryRow label="Scratch Card Prize Paid" value={gbp(m.paid_outs.scratch_prize)} testId="day-end-scratch-prize" />
                  {m.paid_outs.lines.map((l, i) => (
                    <SummaryRow key={`${l.heading}-${i}`} label={l.heading} value={gbp(l.amount)} testId={`day-end-paid-out-${i}`} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Not matched ({m.unmatched.length})</CardTitle>
              <CardDescription>Report headings without a matching department or service. Their shop sales are included in Other Departments.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {m.warnings.map(w => (
                <div key={w} className="flex items-start gap-2 p-2 rounded border border-amber-500/30 bg-amber-500/10 text-sm text-amber-600" data-testid="day-end-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{w}
                </div>
              ))}
              <div className="max-h-80 overflow-y-auto" data-testid="day-end-unmatched">
                {m.unmatched.map((u, i) => (
                  <div key={`${u.name}-${i}`} className="flex justify-between items-center text-sm py-1 border-b border-border/50">
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{u.type.replace('_', ' ')}</Badge>
                      {u.name}
                    </span>
                    <span className="font-mono text-muted-foreground">{gbp(u.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
